import React from "react";
import { MousePointerClick, Users, Coins, Clock, Wallet, Undo2, Banknote, LockOpen } from "lucide-react";
import { formatMoneyCode } from "../../lib/formatMoney";

/**
 * What the program has produced for this practice.
 *
 * MONEY IS CENTS IN, ONE FORMATTER OUT. `stats.commissions.currency` is the settlement
 * currency the SERVER resolved; nothing here holds a code of its own and nothing falls back
 * to one — a missing currency renders as a bare number, which is honest, rather than as a
 * currency the platform may not settle in.
 *
 * THE IDENTITY IS PRINTED, not merely obeyed:
 * `released + on hold + withdrawn = earned − given back`. A dashboard whose figures cannot
 * be reconciled with each other teaches its reader to trust whichever one is largest — and
 * WITHDRAWN is the term that makes it readable by somebody who has actually been paid: the
 * server derives it from the `affiliate_payable` debits of their settled payouts, so
 * "released" drops when money leaves while "earned" does not.
 *
 * RELEASED AND AVAILABLE ARE TWO TILES BECAUSE THEY ANSWER TWO QUESTIONS. `released` is the
 * identity's third term — affiliate money out of hold and not withdrawn. `available` is what
 * the Gateway Wallet will actually pay TODAY, which is `min(released, the wallet's own
 * available)`: the wallet is one balance, and a practice carrying a sales debt, a rolling
 * reserve or a freeze has that discharged before a cent of this money can leave. They were
 * one number, and it could not be both — the page offered money the wallet then refused.
 */
function Stat({ icon: Icon, label, value, sub, tone, testId }) {
  return (
    <div className="bg-white rounded-2xl border border-eh-border-soft p-5" data-testid={testId}>
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-3 ${tone}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="text-2xl font-display text-eh-ink tabular-nums">{value}</div>
      <div className="text-xs text-eh-muted">{label}</div>
      {sub && <div className="text-[11px] text-eh-muted/70 mt-1">{sub}</div>}
    </div>
  );
}

export default function AffiliateStats({ stats, t }) {
  const { commissions, referrals } = stats;
  const money = (cents) => formatMoneyCode(cents, commissions.currency);

  return (
    <section data-testid="affiliate-stats" className="space-y-4">
      <h2 className="font-display text-xl text-eh-ink">{t("aff.stats.title")}</h2>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <Stat
          icon={MousePointerClick}
          label={t("aff.stats.clicksTotal")}
          value={stats.clicksTotal}
          sub={t("aff.stats.clicks30d", { count: stats.clicks30d })}
          tone="bg-blue-100 text-blue-700"
          testId="affiliate-stat-clicks"
        />
        <Stat
          icon={Users}
          label={t("aff.stats.referrals")}
          value={referrals.linked + referrals.active + referrals.expired}
          sub={t("aff.stats.referralsBreakdown", {
            linked: referrals.linked, active: referrals.active, expired: referrals.expired,
          })}
          tone="bg-violet-100 text-violet-700"
          testId="affiliate-stat-referrals"
        />
        <Stat
          icon={Coins}
          label={t("aff.stats.accrued")}
          value={money(commissions.accruedCents)}
          tone="bg-slate-100 text-slate-700"
          testId="affiliate-stat-accrued"
        />
        <Stat
          icon={Clock}
          label={t("aff.stats.onHold")}
          value={money(commissions.onHoldCents)}
          tone="bg-amber-100 text-amber-700"
          testId="affiliate-stat-onhold"
        />
        <Stat
          icon={LockOpen}
          label={t("aff.stats.released")}
          value={money(commissions.releasedCents)}
          tone="bg-teal-100 text-teal-700"
          testId="affiliate-stat-released"
        />
        <Stat
          icon={Wallet}
          label={t("aff.stats.available")}
          value={money(commissions.availableCents)}
          tone="bg-emerald-100 text-emerald-700"
          testId="affiliate-stat-available"
        />
        <Stat
          icon={Banknote}
          label={t("aff.stats.withdrawn")}
          value={money(commissions.withdrawnCents)}
          tone="bg-sky-100 text-sky-700"
          testId="affiliate-stat-withdrawn"
        />
        <Stat
          icon={Undo2}
          label={t("aff.stats.reversed")}
          value={money(commissions.reversedCents)}
          tone="bg-rose-100 text-rose-700"
          testId="affiliate-stat-reversed"
        />
      </div>
      <p className="text-xs text-eh-muted">{t("aff.stats.identity")}</p>
    </section>
  );
}
